<template>
  <v-row class="pr-5" no-gutters>
    <v-col cols="12" class="pt-0 px-0">
      <template v-if="isAnalysisDone && (manholesResultLength || terminalsResultLength)">
        <sidebar-row class="pt-3">
          <v-col cols="auto" class="px-0 pt-0">
            <v-row no-gutters class="text-body-2">
              {{ $i18n.t('sidebar.congestionAnalysis.legend') }}
            </v-row>
          </v-col>
        </sidebar-row>
        <sidebar-row border-bottom>
          <v-col cols="auto" class="px-0 pt-0">
            <v-row
              no-gutters
              class="align-center flex-nowrap text-left"
              v-for="(layer, idx) in congestionProject.layers"
              :key="idx"
            >
              <v-col cols="auto" class="pr-1">
                <layer-style-preview-icon is-icon :layer-type="getLayerType(layer)" :layer-style="layer.style" />
              </v-col>
              <v-col cols="auto" class="text-body-2">
                {{ layer.name }}
              </v-col>
            </v-row>
          </v-col>
        </sidebar-row>
      </template>
      <v-row class="pt-3" no-gutters>
        <v-col cols="12">
          <stepper
            ref="congestionAnalysisStepper"
            editable
            :steps="steps"
            :stepper-props="{
              altLabels: true,
            }"
            :stepper-classes="['elevation-0']"
            :stepper-header-classes="['elevation-0', 'pl-5']"
            :stepper-buttons-classes="['pl-8']"
          >
            <template #1>
              <sidebar-row>
                <v-col cols="8" class="pb-3 px-0">
                  <data-input
                    simple
                    ref="pipeIdButton"
                    v-model="payload.pipe_id"
                    v-bind="pipeIdInput"
                    :featuresSettings="featuresSettings"
                    @pushFeatures="selectTerminal"
                  />
                </v-col>
              </sidebar-row>
              <template v-if="isCongestionSelected">
                <sidebar-row class="pr-5">
                  <v-list class="py-0">
                    <v-list-item class="hover-list-element">
                      <v-list-item-content class="pa-0">
                        <v-list-item-title>
                          <v-subheader class="pl-0 font--text text-body-2" style="height: auto;">
                            ID: {{ payload.pipe_id }}
                          </v-subheader>
                        </v-list-item-title>
                      </v-list-item-content>
                      <v-list-item-action class="flex-row">
                        <v-icon @click="zoomToSelectedPipe(payload.pipe_id)" color="rgba(0,0,0,.7)" size="19px">
                          mdi-magnify
                        </v-icon>
                        <v-icon @click="deleteSelectedPipe(payload.pipe_id)" color="rgba(0,0,0,.7)" size="19px">
                          mdi-close
                        </v-icon>
                      </v-list-item-action>
                    </v-list-item>
                  </v-list>
                </sidebar-row>
              </template>
            </template>
            <template #2>
              <sidebar-row>
                <v-col cols="8" class="pb-3 px-0">
                  <data-input v-model="payload.distance" v-bind="distanceInput" />
                </v-col>
              </sidebar-row>
            </template>
            <template #3>
              <sidebar-row>
                <v-col cols="8" class="pb-3 px-0">
                  <data-input v-model="payload.tolerance" v-bind="toleranceInput" />
                </v-col>
              </sidebar-row>
            </template>
            <template #4>
              <v-row class="pb-5" :class="{ 'no-print': isCardInfoPrint }" no-gutters>
                <v-col cols="1" />
                <v-col cols="auto" v-if="isAnalysisDone">
                  <base-button-subheader
                    @click="resetAnalysis"
                    :disabled="isAnalysisLoading"
                    translation-path="sidebar.congestionAnalysis.reset"
                    cancel
                  />
                </v-col>
                <v-col cols="auto">
                  <base-button-subheader
                    @click="turnOnAnalysis"
                    :translation-path="
                      isAnalysisDone
                        ? 'sidebar.congestionAnalysis.turnOnAnalysisAgain'
                        : 'sidebar.congestionAnalysis.turnOnAnalysis'
                    "
                    :loading="isAnalysisLoading"
                  />
                </v-col>
                <v-spacer />
                <v-col cols="auto" v-if="isAnalysisDone">
                  <base-rounded-button
                    icon="mdi-printer"
                    size="x-small"
                    :tooltip="$i18n.t('button.printAnalysisResult')"
                    @click="$root.$emit('printCardInfo')"
                  />
                </v-col>
              </v-row>
              <template v-if="isAnalysisDone && manholesResultLength > 0">
                <sidebar-row class="pt-3">
                  <v-col cols="auto" class="pt-0 px-0">
                    <v-row class="text-body-2 font-weight-medium" no-gutters>
                      <v-badge
                        :content="manholesResultLength"
                        :value="manholesResultLength > 0"
                        :offset-x="-3"
                        color="error"
                        overlap
                      >
                        {{ $i18n.t('sidebar.congestionAnalysis.manholesWithToleranceList') }}
                      </v-badge>
                    </v-row>
                  </v-col>
                </sidebar-row>
                <template v-if="manholesResultLength > 0">
                  <sidebar-row class="pr-5">
                    <v-list class="py-0">
                      <template v-for="[key, value] in Object.entries(manholesResult)">
                        <v-list-item
                          :key="key"
                          class="hover-list-element"
                          :style="`break-inside: ${isCardInfoPrint ? 'avoid' : 'auto'}`"
                        >
                          <v-list-item-content class="pa-0">
                            <v-list-item-title>
                              <v-subheader class="pl-0 font--text text-body-2" style="height: auto">
                                ID: {{ key }}
                              </v-subheader>
                              <v-subheader class="text-caption font-italic pa-0" style="height: auto">
                                {{
                                  value || value === 0
                                    ? $i18n.t('sidebar.congestionAnalysis.toleranceWithValue', {
                                        suffix: value,
                                      })
                                    : $i18n.t('sidebar.congestionAnalysis.toleranceWithoutValue')
                                }}
                              </v-subheader>
                            </v-list-item-title>
                          </v-list-item-content>
                          <v-list-item-action :class="{ 'no-print': isCardInfoPrint }">
                            <v-icon @click="zoomToManhole(key)" color="rgba(0,0,0,.7)" size="19px">
                              mdi-magnify
                            </v-icon>
                          </v-list-item-action>
                        </v-list-item>
                      </template>
                    </v-list>
                  </sidebar-row>
                </template>
                <sidebar-row>
                  <v-row align="center" class="pt-6" no-gutters>
                    <v-col cols="1" class="ma-0 pa-0" :class="{ 'no-print': isCardInfoPrint }">
                      <v-checkbox class="ma-0 pa-0" v-model="terminalsVisible" hide-details />
                    </v-col>
                    <v-col cols="auto" class="ma-0 pa-0">
                      <sidebar-summary :items="summaryResults" />
                    </v-col>
                  </v-row>
                </sidebar-row>
                <sidebar-row class="pb-3" :class="{ 'no-print': isCardInfoPrint }">
                  <link-underlined
                    translation-path="sidebar.congestionAnalysis.seeListOfTerminals"
                    @click="openTerminalsDialog"
                  />
                </sidebar-row>
              </template>
            </template>
          </stepper>
        </v-col>
      </v-row>
    </v-col>
    <main-large-dialog
      :is-visible.sync="isTerminalsDialogOpened"
      :title="$i18n.t('dialog.title.listOfTerminals')"
      agree-text="dialog.download"
      disagree-text="dialog.close"
      dialog-width="70%"
      no-paddings-y
      no-paddings-x
      header-shadow
      @agree="downloadTerminalsList"
    >
      <template #body>
        <layer-features-data-table
          ref="terminalsTable"
          :layer-id="sewerLayersIds.terminal_layer"
          :empty-without-filter="true"
          :filtered-objects-ids="analysisResult ? analysisResult.terminals : []"
          :table-props="{ 'hide-default-footer': true, 'disable-pagination': true }"
        />
      </template>
    </main-large-dialog>
  </v-row>
</template>

<script>
import { call, get, sync } from 'vuex-pathify';
import congestionAnalysis from '@/mixins/congestionAnalysis';
import stylePreviewUtilities from '@/mixins/stylePreviewUtilities';

export default {
  name: 'CongestionAnalysisTool',
  mixins: [congestionAnalysis, stylePreviewUtilities],
  components: {
    SidebarRow: () => import('@/components/SidebarRow'),
    SidebarSummary: () => import('@/components/SidebarSummary'),
    Stepper: () => import('@/components/Stepper'),
    DataInput: () => import('@/components/DataInput'),
    LayerStylePreviewIcon: () => import('@/components/LayerStylePreviewIcon'),
    LinkUnderlined: () => import('@/components/LinkUnderlined'),
    MainLargeDialog: () => import('@/components/MainLargeDialog'),
    LayerFeaturesDataTable: () => import('@/components/LayerFeaturesDataTable'),
  },
  props: {
    sewerLayersIds: {
      type: Object,
      default: () => {},
    },
    sewerTerminalsDataSource: {
      type: String,
    },
    allSewersAdded: {
      type: Boolean,
      default: false,
    },
  },
  data: () => ({
    payload: {
      distance: 300,
      tolerance: 0.05,
      pipe_id: undefined,
    },
    isAnalysisDone: false,
    isAnalysisLoading: false,
    isTerminalsDialogOpened: false,
    isDownloading: false,
  }),
  computed: {
    analysisResult: sync('congestions/analysisResult'),
    manholesResult: get('congestions/analysisResult@manholes'),
    terminalsResult: get('congestions/analysisResult@terminals'),
    terminalsVisible: sync('congestions/terminalsVisible'),
    isCardInfoPrint: get('tools/toolStatus@isCardInfoPrintActive'),
    manholesResultLength() {
      return Object.keys(this.manholesResult).length;
    },
    terminalsResultLength() {
      return Object.keys(this.terminalsResult).length;
    },
    featuresSettings() {
      return {
        layers: {
          included: [this.sewerLayersIds.pipe_layer],
        },
      };
    },
    isCongestionSelected() {
      return this.payload.pipe_id ? true : false;
    },
    steps() {
      return [
        {
          step: 1,
          translationPath: 'sidebar.congestionAnalysis.congestionLocation',
          backDisabled: true,
          nextDisabled: !this.isCongestionSelected || this.isAnalysisLoading,
        },
        {
          step: 2,
          translationPath: 'sidebar.congestionAnalysis.distance',
          backDisabled: this.isAnalysisLoading,
          nextDisabled: !this.payload.distance || this.isAnalysisLoading,
        },
        {
          step: 3,
          translationPath: 'sidebar.congestionAnalysis.tolerance',
          backDisabled: this.isAnalysisLoading,
          nextDisabled: !this.payload.tolerance || this.isAnalysisLoading,
        },
        {
          step: 4,
          translationPath: 'sidebar.congestionAnalysis.analysis',
          backDisabled: this.isAnalysisLoading,
          nextDisabled: true,
        },
      ];
    },
    pipeIdInput() {
      return {
        dataType: {
          name: 'features',
        },
        translationPath: 'sidebar.congestionAnalysis.selectPipe',
        subtitleTranslationPath: 'sidebar.congestionAnalysis.selectPipeSubtitle',
        disabled: !this.allSewersAdded || this.isCongestionSelected,
        class: 'text-left',
      };
    },
    distanceInput() {
      return {
        dataType: {
          name: 'float',
        },
        allowNegative: false,
        label: this.$i18n.t('sidebar.congestionAnalysis.analyzedDistance'),
        hideDetails: true,
        returnObject: true,
      };
    },
    toleranceInput() {
      return {
        dataType: {
          name: 'float',
        },
        allowNegative: false,
        label: this.$i18n.t('sidebar.congestionAnalysis.toleranceWithUnit'),
        hideDetails: true,
        returnObject: true,
      };
    },
    summaryResults() {
      return [
        {
          name: 'sewerTerminalsCloseToCongestion',
          translationPath: 'sidebar.congestionAnalysis.sewerTerminalsCloseToCongestion',
          color: 'rgb(0,145,234)',
          titleCols: 'auto',
          titleClasses: ['pr-4'],
          value: this.terminalsResultLength,
        },
      ];
    },
  },
  methods: {
    exportLayerFeatures: call('layers/exportLayerFeatures'),
    getTerminalsResult: call('congestions/getTerminalsResult'),
    selectTerminal(features) {
      if (!features || features.length === 0) {
        return;
      }
      const featureId = features[0].id;
      this.payload.pipe_id = featureId;
      this.$refs[`pipeIdButton`]?.$refs['data-input']?.toggleOff();
      this.$store.set('map/ADD_SELECTED_FEATURES!', {
        type: 'selectedFeatures',
        features: {
          [this.sewerLayersIds.pipe_layer]: [featureId],
        },
      });
      this.$root.$emit('setProjectLayerStyle', this.sewerLayersIds.pipe_layer, {
        selectedColors: { selectedColor: '#F00', haloColorSelected: '#F00' },
      });
    },
    deleteSelectedPipe(id = null, layerId = this.sewerLayersIds.pipe_layer) {
      const ids = id ? [id] : [];
      this.payload.pipe_id = undefined;
      this.$store.set('map/DELETE_SELECTED_FEATURES!', {
        type: 'selectedFeatures',
        features: {
          [layerId]: ids,
        },
      });
    },
    zoomToSelectedPipe(id, layerId = this.sewerLayersIds.pipe_layer) {
      this.$root.$emit('zoomToFeature', layerId, 'layers', id);
    },
    zoomToManhole(id, layerId = this.sewerLayersIds.manhole_layer) {
      this.$root.$emit('zoomToFeature', layerId, 'layers', id);
    },
    resetAnalysis() {
      this.isAnalysisDone = false;
      this.$root.$emit('toggleCongestionResultLayers', false);
      this.analysisResult = undefined;
      this.deleteSelectedPipe();
      this.payload = {
        distance: 300,
        tolerance: 0.05,
        pipe_id: undefined,
      };
      this.$refs['congestionAnalysisStepper'].reset();
    },
    async turnOnAnalysis() {
      this.isAnalysisLoading = true;
      try {
        const payload = {
          ...this.payload,
          ...this.sewerLayersIds,
        };
        await this.getTerminalsResult(payload);
        this.$root.$emit('toggleCongestionResultLayers', true, this.sewerLayersIds);
        this.isAnalysisDone = true;
        this.isAnalysisLoading = false;
      } catch (error) {
        this.isAnalysisLoading = false;
      }
    },
    openTerminalsDialog() {
      this.isTerminalsDialogOpened = true;
    },
    async downloadTerminalsList() {
      try {
        this.isDownloading = true;
        const payload = { layer_id: this.sewerLayersIds.terminal_layer, format: 'csv', features_filter: undefined };
        if (this.$refs.terminalsTable?.$data.currentFeaturesFilter) {
          payload.features_filter = this.$refs.terminalsTable.$data.currentFeaturesFilter;
        }
        await this.exportLayerFeatures(payload);
        this.isDownloading = false;
      } catch {
        this.isDownloading = false;
      }
    },
  },
  watch: {
    'sewerLayersIds.pipe_layer'(nV, oV) {
      if ((oV || oV === 0) && oV !== nV) {
        this.$root.$emit('setProjectLayerStyle', oV);
      }
      this.deleteSelectedPipe();
    },
  },
  beforeDestroy() {
    this.$root.$emit('toggleCongestionResultLayers', false);
    this.deleteSelectedPipe();
    if (this.sewerLayersIds.pipe_layer) {
      this.$root.$emit('setProjectLayerStyle', this.sewerLayersIds.pipe_layer);
    }
  },
};
</script>

<style lang="scss" scoped>
.v-list {
  @include border();
  .v-list-item {
    &:not(:last-child) {
      @include border(bottom);
    }
  }
}
</style>
